import {keepPreviousData, queryOptions, QueryClient} from '@tanstack/react-query';
import {operationsAPI} from './api';
import type {QueueFilters, QueueItem, QueueListResponse, QueueSource, QueueState, UpdateJobInput} from './types';

const activeQueueStates: QueueState[] = ['queued', 'running', 'cancel_requested'];

const normalizeFilters = (filters: QueueFilters = {}) => ({
  ...(filters.cursor ? {cursor: filters.cursor} : {}),
  ...(filters.jobKey ? {jobKey: filters.jobKey} : {}),
  ...(filters.limit !== undefined ? {limit: filters.limit} : {}),
  ...(filters.search ? {search: filters.search} : {}),
  ...(filters.source ? {source: [...filters.source].sort()} : {}),
  ...(filters.state ? {state: [...filters.state].sort()} : {}),
  ...(filters.workspaceId ? {workspaceId: filters.workspaceId} : {}),
});

export const operationsKeys = {
  root: () => ['admin', 'operations'] as const,
  jobs: () => [...operationsKeys.root(), 'jobs'] as const,
  queueRoot: () => [...operationsKeys.root(), 'queue'] as const,
  queue: (filters: QueueFilters = {}) => [...operationsKeys.queueRoot(), normalizeFilters(filters)] as const,
};

export const operationsJobsQuery = () => queryOptions({
  queryKey: operationsKeys.jobs(),
  queryFn: ({signal}) => operationsAPI.listJobs(signal),
});

export const operationsQueueQuery = (filters: QueueFilters = {}) => queryOptions({
  queryKey: operationsKeys.queue(filters),
  queryFn: ({signal}) => operationsAPI.listQueue(filters, signal),
  placeholderData: keepPreviousData,
  staleTime: 1000,
  refetchOnWindowFocus: true,
  refetchIntervalInBackground: false,
  refetchInterval: query => query.state.data?.items.some(item => activeQueueStates.includes(item.state)) ? 3000 : 30000,
});

export const invalidateOperationsRoots = async (client: QueryClient): Promise<void> => {
  await client.invalidateQueries({queryKey: operationsKeys.jobs()});
  await client.invalidateQueries({queryKey: operationsKeys.queueRoot()});
};

const replaceCachedQueueItem = (client: QueryClient, replacement: QueueItem): void => {
  client.setQueriesData<QueueListResponse>({queryKey: operationsKeys.queueRoot()}, (current: QueueListResponse | undefined) => {
    if (!current) return current;
    let changed = false;
    const items = current.items.map((item: QueueItem) => {
      if (item.source !== replacement.source || item.runId !== replacement.runId) return item;
      changed = true;
      return replacement;
    });
    return changed ? {...current, items} : current;
  });
};

export const updateJobMutation = (client: QueryClient) => ({
  mutationFn: ({jobKey, input}: {jobKey: string; input: UpdateJobInput}) => operationsAPI.updateJob(jobKey, input),
  onSuccess: () => invalidateOperationsRoots(client),
});

export const runNowMutation = (client: QueryClient) => ({
  mutationFn: (jobKey: string) => operationsAPI.runNow(jobKey),
  onSuccess: () => invalidateOperationsRoots(client),
});

export const cancelQueueItemMutation = (client: QueryClient) => ({
  mutationFn: ({source, runId}: {source: QueueSource; runId: string}) => operationsAPI.cancelQueueItem(source, runId),
  onSuccess: (item: QueueItem, _variables?: {source: QueueSource; runId: string}, _context?: unknown) => replaceCachedQueueItem(client, item),
  onSettled: (_item?: QueueItem, _error?: unknown, _variables?: {source: QueueSource; runId: string}, _context?: unknown) => invalidateOperationsRoots(client),
});

export const recoverQueueItemMutation = (client: QueryClient) => ({
  mutationFn: ({source, runId}: {source: QueueSource; runId: string}) => operationsAPI.recoverQueueItem(source, runId),
  onSuccess: (item: QueueItem, _variables?: {source: QueueSource; runId: string}, _context?: unknown) => replaceCachedQueueItem(client, item),
  onSettled: (_item?: QueueItem, _error?: unknown, _variables?: {source: QueueSource; runId: string}, _context?: unknown) => invalidateOperationsRoots(client),
});
